import { CommonModule } from "@angular/common";
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslateModule } from "@ngx-translate/core";
import { Subscription, filter, startWith } from "rxjs";
import { GuideCatalogEntry, resolveGuideForUrl } from "../../config/guide-catalog";
import { OnboardingTourService } from "../../services/onboarding-tour.service";

/**
 * The single "help" button for in-app guides, living in the top header instead of being
 * repeated in every page toolbar. It resolves the guide of the current route, and hides
 * itself entirely when the page has no guide or when that guide has been switched off in
 * Guide Management — so it never offers a walkthrough that will not run.
 */
@Component({
  selector: "app-header-guide-button",
  standalone: true,
  imports: [CommonModule, MatTooltipModule, TranslateModule],
  templateUrl: "./header-guide-button.component.html",
  styleUrl: "./header-guide-button.component.scss",
})
export class HeaderGuideButtonComponent implements OnInit, OnDestroy {
  @Input() labelled = false;
  guide?: GuideCatalogEntry;

  private routerSub?: Subscription;

  constructor(
    private router: Router,
    private onboardingTour: OnboardingTourService,
  ) {}

  ngOnInit(): void {
    this.routerSub = this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        startWith(null),
      )
      .subscribe(() => {
        this.guide = resolveGuideForUrl(this.router.url);
      });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  /** Visible only when this page has a guide and an admin has left it enabled. */
  get isVisible(): boolean {
    return !!this.guide && this.onboardingTour.isGuideEnabled(this.guide.key);
  }

  replay(): void {
    if (this.guide) {
      this.onboardingTour.restart(this.guide.key);
    }
  }
}
